import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, CardBody, Card, Modal, ModalHeader, ModalBody, Button, Input } from "reactstrap";
import Breadcrumb from "components/Common/Breadcrumb";
import AdminTableContainer from "admin/dispatch/AdminTableContainer";
import { AdminService } from "services/AdminService";
import { AuthData, useAuth } from "context/AuthProvider";
import Swal from "sweetalert2";
import { OrderService } from "services/OrderService";
import { toast } from "react-toastify";
import { useBulkCount } from 'context/BulkCountProvider';
import TableContainer from "components/Common/TableContainer";
interface BulkRequest {
  id: string;

  requestId: string;
  customerName: string;
  pickupAddress: string;
  createdAt: string;
  totalDelivery: number;
  status: string;
  phoneNumber?: string;
  deliveryDate?: string;
  pickupTime?: string;
  deliveryTime?: string;
  deliveryEntries?: DeliveryEntry[];
  isAdminProcess?: boolean;
}

interface DeliveryEntry {
  id: string;
  orderId?: string;
  compName: string;
  custName: string;
  dAddress: string;
  phone: string;
  notes: string;
  qty: number;
  weight: number;
  vehicleType: string;
  rna: string;
  price: number;
  isEditing?: boolean;
  tempPrice?: number;
  totalDistance?: number;
}

const BulkRequestManager: React.FC = () => {
  const [bulkRequests, setBulkRequest] = useState<BulkRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<BulkRequest | null>(null);
  // const [user] = useAuth() as AuthData;
  const [user] = useAuth();
  const [LIMIT_P_C, setLIMIT_P_C] = useState<number>(10);
  const [isOffset_p_c, setOffset_p_c] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(2);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editableEntries, setEditableEntries] = useState<DeliveryEntry[]>([]);
  const { updateBulkCounts } = useBulkCount();
  const toggleViewModal = () => setIsViewModalOpen(!isViewModalOpen);
  const toggleEditModal = () => setIsEditModalOpen(!isEditModalOpen);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(new Set());
  const [bulkPriceInput, setBulkPriceInput] = useState<string>("");
  const [showCheckboxes] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        selectedEntryIds.size > 0 &&
        selectedEntryIds.size < editableEntries.length;
    }
  }, [selectedEntryIds, editableEntries]);
  const fetchBulkRequests = async () => {
    try {
      if (!user?._id) return;

      const response = await AdminService.getbulkRequest(user._id);
      console.log("response", response)
      if (response.success) {
        const mappedRequests: BulkRequest[] = response.data.data.map((item: any) => {
          const deliveryEntries: DeliveryEntry[] = item.deliveries?.map((delivery: any) => ({
            id: delivery._id,
            compName: delivery.deliveryAddress?.companyName || "N/A",
            orderId: delivery.uniqueId || "None",
            custName: delivery.deliveryAddress?.contactName || "N/A",
            dAddress: `${delivery.deliveryAddress?.city || ""}, ${delivery.deliveryAddress?.street || ""}`,
            phone: delivery.deliveryAddress?.contactPhone || "N/A",
            notes: delivery.description || "None",
            qty: delivery.totalItems || 0,
            weight: delivery.totalWeight || 0,
            vehicleType: delivery.vehicleType || "N/A",
            rna: delivery.deliveryOption || "N/A",
            price: delivery.subTotal || 0,
            totalDistance: delivery.totalDistance || 0,
          })) || [];

          return {
            id: item._id,
            requestId: item.bulkId || item._id,
            customerName: item.customerId?.firstName || "Unknown",
            pickupAddress: item.pickupAddress?.city || "N/A",
            // dateTime: new Date(item.createdAt).toLocaleString("en-GB"),
            dateTime: new Date(item.createdAt).toLocaleDateString("en-GB") + "/ " +
              new Date(item.createdAt).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }).replace(":", "."),


            totalDelivery: item.totalDelivery || 0,
            status: item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || "Pending",
            phoneNumber: item.customerId?.phoneNumber || "Unknown",
            deliveryDate: new Date(item.deliveryDateTime).toLocaleDateString("en-GB"),
            pickupTime: new Date(item.pickupDateTime).toLocaleTimeString("en-GB", {
              hour: "2-digit", minute: "2-digit", hour12: false
            }).replace(":", "."),
            deliveryTime: new Date(item.deliveryDateTime).toLocaleTimeString("en-GB", {
              hour: "2-digit", minute: "2-digit", hour12: false
            }).replace(":", "."),
            deliveryEntries
          };
        });
        updateBulkCounts({ pendingBulkCount: response.data.totalCount });
        setBulkRequest(mappedRequests);
        setPageCount(Math.ceil(response.data.totalCount / LIMIT_P_C));
      } else {
        console.error("Error fetching bulk requests:", response.message);
      }
    } catch (error) {
      console.error("Failed to fetch bulk requests:", error);
    }
  };

  useEffect(() => {
    fetchBulkRequests();
  }, []);

  const handleApproveRequest = async (selectedRequest: { id: string }) => {
    if (!selectedRequest || !selectedRequest.id) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to approve this bulk request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!"
    });

    if (result.isConfirmed) {
      try {
        const res = await AdminService.approvebulkRequest(selectedRequest.id);
        if (res.success) {
          Swal.fire("Approved!", "Bulk request has been approved.", "success");
          fetchBulkRequests();
        } else {
          Swal.fire("Failed", res.message || "Approval failed.", "error");
        }
      } catch (error) {
        console.error("Approval error:", error);
        Swal.fire("Error", "Something went wrong while approving the request.", "error");
      }
    }
  };

  const handleProcessRequest = async (selectedRequest: { id: string }) => {
    if (!selectedRequest || !selectedRequest.id) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to Process this bulk request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!"
    });

    if (result.isConfirmed) {
      try {
        const res = await AdminService.processbulkRequest(selectedRequest.id);
        if (res.success) {
          Swal.fire("Processed!", "Bulk request has been Process.", "success");
          fetchBulkRequests();
        } else {
          Swal.fire("Failed", res.error || "Process failed.", "error");
        }
      } catch (error) {
        console.error("Process error:", error);
        Swal.fire("Error", "Something went wrong while approving the request.", "error");
      }
    }
  };

  const handleViewClick = (request: BulkRequest) => {
    setSelectedRequest(request);
    setIsViewModalOpen(true);
  };

  const handleEditClick = (request: BulkRequest) => {
    setSelectedRequest(request);
    setEditableEntries(
      request.deliveryEntries?.map(entry => ({
        ...entry,
        isEditing: false,
        tempPrice: entry.price
      })) || []
    );
    setIsEditModalOpen(true);
  };

  const toggleEditRow = (index: number) => {
    const updatedEntries = [...editableEntries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      isEditing: !updatedEntries[index].isEditing,
      tempPrice: updatedEntries[index].price
    };
    setEditableEntries(updatedEntries);
  };

  const handlePriceChange = (index: number, value: number | string) => {
    const updatedEntries = [...editableEntries];

    updatedEntries[index].tempPrice = typeof value === 'string' ? parseFloat(value) || 0 : value;
    setEditableEntries(updatedEntries);
  };
  // const handleSaveRow = async (index: number) => {
  //   if (!selectedRequest || !editableEntries[index]) return;

  //   const entry = editableEntries[index];
  //   const newPrice = entry.tempPrice ?? entry.price;

  //   try {
  //     const response = await AdminService.updateDeliveryPrice({
  //       deliveryId: entry.id,
  //       price: newPrice
  //     });

  //     if (response.data?.success) { // Check response.data.success
  //       const updatedEntries = [...editableEntries];
  //       updatedEntries[index] = {
  //         ...updatedEntries[index],
  //         price: newPrice,
  //         isEditing: false
  //       };
  //       setEditableEntries(updatedEntries);

  //       // Update the main bulk requests data
  //       const updatedBulkRequests = bulkRequests.map(request => {
  //         if (request.id === selectedRequest.id) {
  //           const updatedEntries = request.deliveryEntries?.map(e => {
  //             if (e.id === entry.id) {
  //               return { ...e, price: newPrice };
  //             }
  //             return e;
  //           });
  //           return { ...request, deliveryEntries: updatedEntries };
  //         }
  //         return request;
  //       });
  //       setBulkRequest(updatedBulkRequests);
  //       Swal.fire("Success!", "Price has been updated successfully.", "success");

  //     } else {
  //       Swal.fire("Error", response.data?.message || "Update failed", "error");
  //     }
  //   } catch (error) {
  //     console.error("Error:", error);
  //     Swal.fire("Error", "Update failed", "error");
  //   }
  // };

  const handleBulkApplyPrice = async () => {
    if (selectedEntryIds.size === 0 || !bulkPriceInput) return;

    const price = parseFloat(bulkPriceInput);
    if (isNaN(price) || price <= 0) return;

    try {
      const response = await AdminService.updateDeliveryPrice({
        deliveryIds: Array.from(selectedEntryIds),
        price
      });

      if (response.data?.success) {
        // 1. Update editableEntries
        const updated = editableEntries.map(en =>
          selectedEntryIds.has(en.id)
            ? { ...en, price, tempPrice: price, isEditing: false }
            : en
        );
        setEditableEntries(updated);

        // 2. Update main list so the parent table reflects new prices instantly
        const patched = bulkRequests.map(br =>
          br.id === selectedRequest?.id
            ? {
              ...br,
              deliveryEntries: br.deliveryEntries?.map(de =>
                selectedEntryIds.has(de.id) ? { ...de, price } : de
              )
            }
            : br
        );
        setBulkRequest(patched);

        Swal.fire(
          "Success!",
          `${response.data.modifiedCount || selectedEntryIds.size} price(s) updated`,
          "success"
        );

        // reset
        setBulkPriceInput("");
        setSelectedEntryIds(new Set());
      } else {
        Swal.fire("Error", response.data?.message || "Update failed", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Update failed", "error");
    }
  };


  const deliveryEntriesColumns = useMemo(
    () => [
      { Header: "Order Id", accessor: "orderId" },
      { Header: "Comp. Name", accessor: "compName" },
      { Header: "Cust. Name", accessor: "custName" },
      { Header: "D. Address", accessor: "dAddress" },
      { Header: "Phone", accessor: "phone" },
      { Header: "Notes", accessor: "notes" },
      { Header: "Qty", accessor: "qty" },
      { Header: "Weight (bp)", accessor: "weight" },
      { Header: "Vehicle.T", accessor: "vehicleType" },
      {
        Header: "RNA",
        accessor: "rna",
        Cell: ({ value }: { value: string | undefined }) => value
          ? value.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())
          : "-"
      },
      { Header: "Distance", accessor: "totalDistance" },
      {
        Header: "Subtotal (USD)",
        accessor: "price",
        Cell: ({ value }: { value: number }) => (
          <span>${value.toFixed(2)}</span>
        )
      },
    ],
    []
  );

  const handleDownloadPdf = (bulkRequestId: string) => {
    if (!bulkRequestId) {
      toast.error("Invalid request ID.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }
    console.log("*********", bulkRequestId)
    OrderService.generateBulkOrderPdf(bulkRequestId).then(res => {
      console.log("thuis sthe ", res)
      if (res) {
        try {
          const pdfBlob = new Blob([res as unknown as ArrayBuffer], { type: "application/pdf" });
          const pdfURL = URL.createObjectURL(pdfBlob);
          window.open(pdfURL, "_blank");
        } catch {
          toast.error("Something went wrong while downloading. Please try again later.", {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
          });
        }
      } else {
        toast.error("Connection error. Please try again later.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
        });
      }
    });
  };

  const editableDeliveryEntriesColumns = useMemo(
    () => [
      //   {
      //   Header: () => (
      //     <Input
      //       type="checkbox"

      //       checked={
      //         editableEntries.length > 0 &&
      //         selectedEntryIds.size === editableEntries.length
      //       }
      //       ref={(input) => {
      //         if (input) {
      //           input.indeterminate =
      //             selectedEntryIds.size > 0 &&
      //             selectedEntryIds.size < editableEntries.length;
      //         }
      //       }}
      //       onChange={(e) => {
      //         if (e.target.checked) {
      //           setSelectedEntryIds(new Set(editableEntries.map((en) => en.id)));
      //         } else {
      //           setSelectedEntryIds(new Set());
      //         }
      //       }}
      //     />
      //   ),
      //   id: "select",
      //   Cell: ({ row }: { row: { original: DeliveryEntry } }) => {
      //     const isChecked = selectedEntryIds.has(row.original.id);

      //     return (
      //       <Input
      //         type="checkbox"
      //         checked={isChecked}
      //         onChange={(e) => {
      //           e.stopPropagation();
      //           const newSet = new Set(selectedEntryIds);

      //           if (newSet.has(row.original.id)) {
      //             newSet.delete(row.original.id);
      //           } else {
      //             newSet.add(row.original.id);
      //           }

      //           setSelectedEntryIds(newSet);
      //         }}
      //         onClick={(e) => e.stopPropagation()}
      //       />
      //     );
      //   },
      // },
      {
        Header: () => (
          <Input
            type="checkbox"
            innerRef={headerCheckboxRef}
            checked={
              editableEntries.length > 0 &&
              selectedEntryIds.size === editableEntries.length
            }
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedEntryIds(new Set(editableEntries.map((en) => en.id)));
              } else {
                setSelectedEntryIds(new Set());
              }
            }}
          />
        ),
        id: "select",
        Cell: ({ row }: { row: { original: DeliveryEntry } }) => {
          const isChecked = selectedEntryIds.has(row.original.id);

          return (
            <Input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                const newSet = new Set(selectedEntryIds);

                if (newSet.has(row.original.id)) {
                  newSet.delete(row.original.id);
                } else {
                  newSet.add(row.original.id);
                }

                setSelectedEntryIds(newSet);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
      },


      { Header: "Order Id", accessor: "orderId" },
      { Header: "Comp. Name", accessor: "compName" },
      { Header: "Cust. Name", accessor: "custName" },
      { Header: "D. Address", accessor: "dAddress" },

      { Header: "Notes", accessor: "notes" },

      {
        Header: "RNA",
        accessor: "rna",
        Cell: ({ value }: { value: string | undefined }) => value
          ? value.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())
          : "-"
      },
      { Header: "Vehicle.T", accessor: "vehicleType" },
      { Header: "Qty", accessor: "qty" },
      { Header: "Weight (bp)", accessor: "weight" },
      { Header: "Distance", accessor: "totalDistance" },
      {
        Header: "Subtotal (USD)",
        accessor: "price",
        Cell: ({ row }: { row: { index: number, original: DeliveryEntry } }) => {
          const entry = editableEntries[row.index];
          return entry.isEditing ? (
            <div className="input-group input-group-sm">
              <span className="input-group-text">$</span>
              <input
                type="text" // Changed from "number" to "text"
                className="form-control"
                value={entry.tempPrice || entry.price}
                onChange={(e) => {

                  const value = e.target.value;
                  handlePriceChange(row.index, value === '' ? 0 : (value));

                }}


              />
            </div>
          ) : (
            <span>${entry.price.toFixed(2)}</span>
          );
        }
      },

    ],

    [editableEntries, selectedEntryIds]
  );

  const mainColumns = useMemo(
    () => [
      { Header: "Request ID", accessor: "requestId" },
      { Header: "Cust. name", accessor: "customerName" },
      { Header: "Pickup Address", accessor: "pickupAddress" },
      { Header: "Date/time", accessor: "dateTime" },
      { Header: "Total Delivery", accessor: "totalDelivery" },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }: { value: string }) => (
          <span
            className={`badge rounded-pill d-inline-block text-center px-3 py-1 fw-semibold text-xs
              ${value === "Live"
                ? "bg-primary text-white"
                : value === "Approved"
                  ? "bg-success text-white"
                  : value === "Pending"
                    ? "bg-warning text-white"
                    : "bg-secondary text-white"
              }`}
            style={{ minWidth: "80px", fontSize: "0.75rem" }}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Actions",
        id: "actions",
        Cell: ({ row }: { row: { original: BulkRequest } }) => (
          <div className="d-flex align-items-center gap-2">
            <i
              className="bx bx-show text-secondary fs-5"
              role="button"
              title="View"
              onClick={() => handleViewClick(row.original)}
            ></i>
            <i
              className="bx bx-edit text-secondary fs-5"
              role="button"
              title="Edit"
              onClick={() => handleEditClick(row.original)}
            ></i>
            <i
              className={`fas fa-sync-alt fs-6 ${row.original.isAdminProcess ? "text-muted opacity-50" : "text-warning"
                }`}
              // onClick={() => {
              //   if (!row.original.isAdminProcess) {
              //     handleProcessRequest({ id: row.original.id });
              //   }
              // }}
              onClick={() => { void handleProcessRequest({ id: row.original.id }); }}
              style={{
                cursor: row.original.isAdminProcess ? "not-allowed" : "pointer",
                pointerEvents: row.original.isAdminProcess ? "none" : "auto"
              }}
              title={
                row.original.isAdminProcess
                  ? "Already processed"
                  : "Click to process request"
              }
            />

            <button
              className="btn btn-sm"
              style={{
                backgroundColor: "#ff875a",
                color: "#fff",
                fontWeight: 500,
                padding: "3px 10px",
                fontSize: "0.75rem",
                borderRadius: "4px",
              }}
              // onClick={() => handleApproveRequest({ id: row.original.id })}
              onClick={() => { void handleApproveRequest({ id: row.original.id }); }}
              disabled={row.original.status.toLowerCase() === "approved"}
            >
              Approve
            </button>
          </div>
        ),
      }
    ],
    []
  );

  document.title = "Bulk Requests | Zulu Courier";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Bulk Request Manager" breadcrumbItem="Bulk Requests" />

        <Col lg={12}>
          <Card>
            <CardBody>
              {bulkRequests.length > 0 ? (
                <TableContainer
                  columns={mainColumns}
                  data={bulkRequests}
                  pagelimit={1}
                  handlePageChange={() => { }}
                  handleSearch={() => { }}
                  handleColumnChange={() => { }}
                  selectedColumns={LIMIT_P_C}
                  showColumnChange={true}
                  customPageSize={50}
                  className="custom-header-css"
                  isJobListGlobalFilter={false}
                  // isChangeDateList={false}
                  // isChangeDriverList={false}
                  // isModified={false}
                  // setIsModified={() => { }}
                  isPDFGenerate={false}
                  ispopup={false}
                  isAddressBook={false}
                  hancleDeleteRecords={undefined}
                  handleSelect={undefined}
                  selectedID={undefined} currentPage={undefined} isGlobalFilter={undefined} isAddOptions={undefined} isAddUserList={undefined} handleOrderClicks={undefined} handleUserClick={undefined} handleCustomerClick={undefined} isAddCustList={undefined} hasCheckBox={undefined} handleSendEmail={undefined} // getChangedStatusData={() => { }}
                  footerData={undefined}                  // currentStatus={""}
                // isChangeMultipleStatus={false}
                />
              ) : (
                <Row className="justify-content-md-center justify-content-center align-items-center">
                  <Col className="col-md-auto">
                    <div>No Bulk Requests</div>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* View Modal */}
        <Modal
          isOpen={isViewModalOpen}
          toggle={toggleViewModal}
          size="xl"
          style={{ maxWidth: '1500px' }}
          contentClassName="custom-modal-content"
        >
          <ModalHeader toggle={toggleViewModal}></ModalHeader>
          <ModalBody>
            <div className="px-5 py-2">
              <h4 className="bg-primary bg-opacity-25 popover-header py-3 text-center fw-bold">
                Bulk Request View
              </h4>
              {selectedRequest && (
                <div className="request-details">
                  <section className="mb-4">
                    <h6 className="my-3">Customer Info</h6>
                    <div className="row ps-3">
                      <div className="col-md-3 mb-2"><div>Name:</div></div>
                      <div className="col-md-3 mb-2"><div>{selectedRequest.customerName}</div></div>
                      <div className="col-md-3 mb-2"><div>Phone number:</div></div>
                      <div className="col-md-3 mb-2"><div>{selectedRequest.phoneNumber}</div></div>
                    </div>
                  </section>

                  <section className="mb-4">
                    <h6 className="my-3">Pickup Details</h6>
                    <div className="row ps-3">
                      <div className="col-md-3 mb-2"><div>Pickup address:</div></div>
                      <div className="col-md-3 mb-2"><div>{selectedRequest.pickupAddress}</div></div>
                      <div className="col-md-3 mb-2"><div>Pick-Up date:</div></div>
                      <div className="col-md-3 mb-2"><div>{selectedRequest.deliveryDate}</div></div>
                    </div>
                    <div className="row ps-3">
                      <div className="col-md-3 mb-2"><div>Pick-Up Time:</div></div>
                      <div className="col-md-3 mb-2"><div>{selectedRequest.pickupTime}</div></div>
                      <div className="col-md-3 mb-2"><div>Delivery Time:</div></div>
                      <div className="col-md-3 mb-2"><div>{selectedRequest.deliveryTime}</div></div>
                    </div>
                    <div className="row ps-3">
                      <div className="col-md-3 mb-2"><div>Vehicle Type:</div></div>
                      <div className="col-md-3 mb-2">  <div>{selectedRequest?.deliveryEntries?.[0]?.vehicleType}</div></div>
                      <div className="col-md-3 mb-2"><div>Delivery Option:</div></div>
                      <div className="col-md-3 mb-2">
                        <div>
                          {selectedRequest?.deliveryEntries?.[0]?.rna
                            ? selectedRequest.deliveryEntries[0].rna
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (char) => char.toUpperCase())
                            : "-"}
                        </div>
                      </div>

                    </div>
                  </section>

                  <section className="mb-4">
                    <h6 className="mb-3">Delivery Entries</h6>
                    <div className="table-responsive">
                      <TableContainer
                        columns={deliveryEntriesColumns}
                        data={selectedRequest.deliveryEntries || []}
                        pagelimit={1}
                        handlePageChange={() => { }}
                        handleSearch={false}
                        handleColumnChange={() => { }}
                        selectedColumns={10}
                        showColumnChange={false}
                        customPageSize={100}
                        className="mb-3"
                        isJobListGlobalFilter={false}
                        // isChangeDateList={false}
                        // isChangeDriverList={false}
                        // isModified={false}
                        // setIsModified={() => { }}
                        isPDFGenerate={false}
                        ispopup={false}
                        isAddressBook={false}
                        hancleDeleteRecords={undefined}
                        handleSelect={undefined}
                        selectedID={undefined} currentPage={undefined} isGlobalFilter={undefined} isAddOptions={undefined} isAddUserList={undefined} handleOrderClicks={undefined} handleUserClick={undefined} handleCustomerClick={undefined} isAddCustList={undefined} hasCheckBox={undefined} handleSendEmail={undefined} // getChangedStatusData={undefined}
                        footerData={undefined}                        // currentStatus={""}
                      // isChangeMultipleStatus={false}
                      />
                    </div>
                    <div className="text-end mt-4">
                      <Button
                        color="primary"
                        onClick={() => handleDownloadPdf(selectedRequest.id)}
                        className="px-4"
                      >
                        <i className="bx bx-download me-2"></i>
                        Download
                      </Button>
                    </div>
                  </section>
                </div>
              )}
            </div>
          </ModalBody>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          toggle={toggleEditModal}
          size="xl"
          style={{ maxWidth: '1500px' }}
          contentClassName="custom-modal-content"
        >
          <ModalHeader toggle={toggleEditModal}></ModalHeader>
          <ModalBody>
            <div className="px-5 py-2">
              <h4 className="bg-primary bg-opacity-25 popover-header py-3 text-center fw-bold">
                Bulk Price Edit
              </h4>
              {selectedRequest && (
                <div className="request-details">
                  <section className="my-4">
                    {/* <h6 className="mb-3">Edit Delivery Prices</h6> */}
                    <div className="d-flex align-items-center justify-content-end gap-3 mb-3 w-100">
                      <label className="fw-bold mb-0">Bulk price:</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: 120 }}
                        placeholder="0.00"
                        value={bulkPriceInput}
                        onChange={e => setBulkPriceInput(e.target.value)}
                      />
                      <Button
                        color="primary"
                        size="sm"
                        //  onClick={handleBulkApplyPrice}
                        onClick={() => { void handleBulkApplyPrice(); }}
                        disabled={selectedEntryIds.size === 0}
                      >
                        Apply
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <TableContainer
                        columns={editableDeliveryEntriesColumns}
                        data={editableEntries || []}
                        pagelimit={1}
                        handlePageChange={() => { }}
                        handleSearch={false}
                        handleColumnChange={() => { }}
                        selectedColumns={6}
                        showColumnChange={false}
                        customPageSize={100}
                        className="mb-3"
                        isJobListGlobalFilter={false}
                        isPDFGenerate={false}
                        ispopup={false}
                        isAddressBook={false}
                        hancleDeleteRecords={undefined}
                        handleSelect={undefined}
                        selectedID={undefined}
                        currentPage={undefined}
                        isGlobalFilter={undefined}
                        isAddOptions={undefined}
                        isAddUserList={undefined}
                        handleOrderClicks={undefined}
                        handleUserClick={undefined}
                        handleCustomerClick={undefined}
                        isAddCustList={undefined}
                        hasCheckBox={undefined}
                        handleSendEmail={undefined} footerData={undefined} />
                    </div>
                  </section>


                </div>
              )}
            </div>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
};

export default BulkRequestManager;